// React
import { useEffect, useState } from "react";
// MUI
import { GridColDef, useGridApiRef } from "@mui/x-data-grid-premium";
// Third Party
import { v4 as uuidv4 } from "uuid";

// Hooks
import { useLocation } from "@Common/hooks/Location/useLocation";

// Helpers
import { getDateInUTC, getFormattedDate } from "@dealerwave/dw-layer-date-converter";

// Components
import DataSmartTable from "@Layouts/components/Tables/DataSmartTable";
import CommonToolbar from "@Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import AssignmentMessages from "@/PayPlans/components/Assignments/AssignmentMessages";

// Constants
import { DATE_FORMAT } from "@/Constants/date";

type AssignmentStatusHistoryProps = {
	data: {
		acknowledgementDate: string;
		row: {
			id: string;
			acknowledgementStatus: {
				status: string;
				statusDate: string;
			}[];
		};
	};
};
export default function AssignmentStatusHistory(props: AssignmentStatusHistoryProps) {
	const apiRef = useGridApiRef();

	const { timezone } = useLocation() ?? {};

	// States
	const [row, setRow] = useState<{ id: string; _id: string; acknowledgementStatus: { status: string; statusDate: string }[] }[]>([]);

	const columns: GridColDef[] = [
		{ field: "_id", headerName: "ID", width: 50, filterable: false },
		{
			field: "planId",
			headerName: "Pay Plan ID",
			width: 140,
			filterable: false,
		},
		{
			field: "assignmentDate",
			headerName: "Assignment Date",
			type: "date",
			width: 175,
			valueGetter: (value) => value && getDateInUTC(value),
			valueFormatter: (value) => getFormattedDate(value, timezone, DATE_FORMAT.monthAbbrDatePadded),
		},
		{
			field: "assignedBy",
			headerName: "Assigned By",
			width: 150,
		},
		{
			field: "effectiveDate",
			headerName: "Effective Date",
			type: "date",
			width: 200,
			valueGetter: (value) => value && getDateInUTC(value),
			valueFormatter: (value) => getFormattedDate(value, timezone, DATE_FORMAT.monthAbbrDatePadded),
		},
		{
			field: "acknowledgementStatus",
			headerName: "Acknowledgement Status",
			width: 250,
			renderCell: (localRow) => {
				if (!localRow?.value.length) {
					return null;
				}

				const lastItem = localRow.value[localRow.value.length - 1];
				return (
					<AssignmentMessages
						status={lastItem.status}
						statusDate={lastItem.statusDate}
						acknowledgementDate={props?.data?.acknowledgementDate}
					/>
				);
			},
		},
	];

	useEffect(() => {
		const rowDefinition = props?.data?.row;
		if (rowDefinition && typeof rowDefinition === "object" && !Array.isArray(rowDefinition)) {
			const { acknowledgementStatus, ...rest } = rowDefinition;
			const newArray = acknowledgementStatus.map((status) => {
				const newId = uuidv4(); // Generate a new UUID for each item
				return { ...rest, id: newId, _id: newId, acknowledgementStatus: [status] };
			});
			setRow(newArray);
		}
	}, [props?.data?.row]);

	return (
		<DataSmartTable
			columns={columns}
			rows={row || []}
			rowHeight={28}
			enableCustomScrollbars
			apiRef={apiRef}
			disableColumnMenu
			disableMultipleRowSelection
			disableRowSelectionOnClick
			columnVisibilityModel={{
				_id: false,
			}}
			slots={{
				toolbar: CommonToolbar,
				noRowsOverlay: NoRowsOverlay,
			}}
			slotProps={{
				toolbar: {
					filter: {
						hide: false,
						disabled: false,
					},
					search: {
						data: row || [],
						hide: false,
						disabled: false,
					},
				},
				noRowsOverlay: {
					title: "No Pay Plan Assignment History",
				},
			}}
			hideFooter
			customProps={{
				tableContainerStyle: { height: "100%", width: "100%" },
				tableClassName: {
					"functional-table": true,
				},
				expandableRows: false,
				hide: false,
			}}
		/>
	);
}
